import React from 'react';
import InsightsDateRangeFilter from './InsightsDateRangeFilter';
import {
    GK_SITUATION_KEYS,
    GK_SITUATION_LABELS,
    GkSituationKey,
    isAllSituations,
    ZoneCounts,
} from './insightsGoalkeeperZones';
import { MapSide } from './insightsMapData';
import { BundleFilterOptions, MatchBundleFilterSelection } from './insightsMatchBundleFilters';
import { MAP_PERIOD_KEYS, MAP_PERIOD_LABELS, MapPeriodKey } from './insightsMatchStats';
import {
    SHOT_FAKE_KEYS,
    SHOT_FAKE_LABELS,
    SHOT_FOUL_KEYS,
    SHOT_FOUL_LABELS,
    SHOT_TECHNIQUE_KEYS,
    SHOT_TECHNIQUE_LABELS,
    ShotFakeKey,
    ShotFoulKey,
    ShotTechniqueKey,
} from './insightsShotKindFilters';
import InsightsMultiSelectDropdown from './InsightsMultiSelectDropdown';
import InsightsShotMap from './InsightsShotMap';

interface Props {
    title: string;
    hint: string;
    colorMode: 'keeper' | 'shooter';
    selected: GkSituationKey[];
    onSelectedChange: (next: GkSituationKey[]) => void;
    /** null = all periods; [] = none. */
    periodSelected?: MapPeriodKey[] | null;
    onPeriodSelectedChange?: (next: MapPeriodKey[] | null) => void;
    showZoneStats: boolean;
    onShowZoneStatsChange: (show: boolean) => void;
    showShotMarks: boolean;
    onShowShotMarksChange: (show: boolean) => void;
    /** Competition / date / match options from the loaded sample. */
    bundleFilterOptions: BundleFilterOptions;
    bundleFilters: MatchBundleFilterSelection;
    onBundleFiltersChange: (next: MatchBundleFilterSelection) => void;
    /** When true, parent already filtered by competition/date/match - hide those controls. */
    hideScopeFilters?: boolean;
    /** Optional player multi-select (match maps). null = all; [] = none. */
    playerOptions?: { value: number; label: string }[];
    playerSelected?: number[] | null;
    onPlayerSelectedChange?: (next: number[] | null) => void;
    /** Technique / foul / fakes. null = all; [] = none. */
    techniqueSelected?: ShotTechniqueKey[] | null;
    onTechniqueSelectedChange?: (next: ShotTechniqueKey[] | null) => void;
    foulSelected?: ShotFoulKey[] | null;
    onFoulSelectedChange?: (next: ShotFoulKey[] | null) => void;
    fakeSelected?: ShotFakeKey[] | null;
    onFakeSelectedChange?: (next: ShotFakeKey[] | null) => void;
    filterSummary: React.ReactNode;
    statsStrip: React.ReactNode;
    sampleNote?: React.ReactNode;
    goalMap: MapSide;
    poolMap: MapSide;
    goalZoneCounts?: ZoneCounts;
    poolZoneCounts?: ZoneCounts;
    goalEmptyHint: string;
    poolEmptyHint: string;
}

/**
 * Shared player-page map layout: situation dropdown + one full-width goalmouth
 * + one full-width half-pool. Used by both GK and shooter blocks.
 */
const InsightsSituationMapsShell: React.FC<Props> = ({
    title,
    hint,
    colorMode,
    selected,
    onSelectedChange,
    periodSelected = null,
    onPeriodSelectedChange,
    showZoneStats,
    onShowZoneStatsChange,
    showShotMarks,
    onShowShotMarksChange,
    bundleFilterOptions,
    bundleFilters,
    onBundleFiltersChange,
    hideScopeFilters,
    playerOptions,
    playerSelected,
    onPlayerSelectedChange,
    techniqueSelected = null,
    onTechniqueSelectedChange,
    foulSelected = null,
    onFoulSelectedChange,
    fakeSelected = null,
    onFakeSelectedChange,
    filterSummary,
    statsStrip,
    sampleNote,
    goalMap,
    poolMap,
    goalZoneCounts,
    poolZoneCounts,
    goalEmptyHint,
    poolEmptyHint,
}) => {
    const allActive = isAllSituations(selected);
    const competitionIds = bundleFilters.competitionIds ?? null;
    const matchIds = bundleFilters.matchIds ?? null;
    const showCompetitions = !hideScopeFilters && bundleFilterOptions.competitions.length > 0;
    const showDates = !hideScopeFilters && bundleFilterOptions.dates.length > 0;
    const showMatches = !hideScopeFilters && bundleFilterOptions.matches.length > 0;
    const showBundleFilters = showCompetitions || showDates || showMatches;

    const setCompetitions = (next: number[] | null) =>
        onBundleFiltersChange({ ...bundleFilters, competitionIds: next });
    const setDateRange = (next: { dateFrom: string | null; dateTo: string | null }) =>
        onBundleFiltersChange({
            ...bundleFilters,
            dateFrom: next.dateFrom,
            dateTo: next.dateTo,
        });
    const setMatches = (next: number[] | null) =>
        onBundleFiltersChange({ ...bundleFilters, matchIds: next });

    // Dropdown: null = all situations; [] = none; otherwise the keys.
    const situationSelected: GkSituationKey[] | null = allActive
        ? null
        : selected.length === 0
        ? []
        : selected;
    const onSituationChange = (next: GkSituationKey[] | null) => {
        if (next === null || next.length === GK_SITUATION_KEYS.length) {
            onSelectedChange([...GK_SITUATION_KEYS]);
            return;
        }
        onSelectedChange(next);
    };

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>{title}</h2>
                <span>{hint}</span>
            </div>

            {statsStrip}
            {sampleNote}

            {showBundleFilters ? (
                <div className="insightsMapFilters">
                    <div className="insightsMapFilterBody">
                        {showCompetitions ? (
                            <InsightsMultiSelectDropdown
                                label="Competition"
                                options={bundleFilterOptions.competitions.map((c) => ({
                                    value: c.id,
                                    label: c.name,
                                }))}
                                selected={competitionIds}
                                onChange={setCompetitions}
                            />
                        ) : null}

                        {showDates ? (
                            <InsightsDateRangeFilter
                                dateFrom={bundleFilters.dateFrom}
                                dateTo={bundleFilters.dateTo}
                                onChange={setDateRange}
                            />
                        ) : null}

                        {showMatches ? (
                            <InsightsMultiSelectDropdown
                                label="Match"
                                options={bundleFilterOptions.matches.map((m) => ({
                                    value: m.id,
                                    label: m.label,
                                }))}
                                selected={matchIds}
                                onChange={setMatches}
                                listClassName="insightsMultiSelectListTall"
                            />
                        ) : null}
                    </div>
                </div>
            ) : null}

            <div className="insightsMapToolbar">
                <div className="insightsMapToolbarFilters">
                    <InsightsMultiSelectDropdown
                        label="Situation"
                        options={GK_SITUATION_KEYS.map((key) => ({
                            value: key,
                            label: GK_SITUATION_LABELS[key],
                        }))}
                        selected={situationSelected}
                        onChange={onSituationChange}
                    />
                    {onPeriodSelectedChange ? (
                        <InsightsMultiSelectDropdown
                            label="Period"
                            options={MAP_PERIOD_KEYS.map((key) => ({
                                value: key,
                                label: MAP_PERIOD_LABELS[key],
                            }))}
                            selected={periodSelected ?? null}
                            onChange={onPeriodSelectedChange}
                        />
                    ) : null}
                    {playerOptions && playerOptions.length > 0 && onPlayerSelectedChange ? (
                        <InsightsMultiSelectDropdown
                            label="Player"
                            options={playerOptions}
                            selected={playerSelected ?? null}
                            onChange={onPlayerSelectedChange}
                            listClassName="insightsMultiSelectListTall"
                        />
                    ) : null}
                    {onTechniqueSelectedChange ? (
                        <InsightsMultiSelectDropdown
                            label="Technique"
                            options={SHOT_TECHNIQUE_KEYS.map((key) => ({
                                value: key,
                                label: SHOT_TECHNIQUE_LABELS[key],
                            }))}
                            selected={techniqueSelected}
                            onChange={onTechniqueSelectedChange}
                        />
                    ) : null}
                    {onFoulSelectedChange ? (
                        <InsightsMultiSelectDropdown
                            label="Foul"
                            options={SHOT_FOUL_KEYS.map((key) => ({
                                value: key,
                                label: SHOT_FOUL_LABELS[key],
                            }))}
                            selected={foulSelected}
                            onChange={onFoulSelectedChange}
                        />
                    ) : null}
                    {onFakeSelectedChange ? (
                        <InsightsMultiSelectDropdown
                            label="Fakes"
                            options={SHOT_FAKE_KEYS.map((key) => ({
                                value: key,
                                label: SHOT_FAKE_LABELS[key],
                            }))}
                            selected={fakeSelected}
                            onChange={onFakeSelectedChange}
                        />
                    ) : null}
                </div>
                <div className="insightsMapToggles">
                    <button
                        type="button"
                        className="insightsZoneStatsToggle"
                        aria-pressed={showZoneStats}
                        onClick={() => onShowZoneStatsChange(!showZoneStats)}
                    >
                        {showZoneStats ? 'Hide zone %' : 'Show zone %'}
                    </button>
                    <button
                        type="button"
                        className="insightsZoneStatsToggle"
                        aria-pressed={showShotMarks}
                        onClick={() => onShowShotMarksChange(!showShotMarks)}
                    >
                        {showShotMarks ? 'Hide shot marks' : 'Show shot marks'}
                    </button>
                </div>
            </div>

            <div className="insightsMapFilterSummary">{filterSummary}</div>

            <div className="insightsMapStack">
                <InsightsShotMap
                    side={goalMap}
                    title="Goalmouth"
                    surface="goal"
                    fullBleed
                    colorMode={colorMode}
                    showZones
                    showZoneStats={showZoneStats}
                    showShotMarks={showShotMarks}
                    zoneCounts={goalZoneCounts}
                    emptyHint={goalEmptyHint}
                />
                <InsightsShotMap
                    side={poolMap}
                    title="Pool location"
                    surface="pool"
                    fullBleed
                    colorMode={colorMode}
                    cropPoolTopHalf
                    showZones
                    showZoneStats={showZoneStats}
                    showShotMarks={showShotMarks}
                    zoneCounts={poolZoneCounts}
                    emptyHint={poolEmptyHint}
                />
            </div>
        </section>
    );
};

export default InsightsSituationMapsShell;
